/** /sessions/ — CPToken management. Token values are fingerprints unless revealed. */
import { apiDelete, apiGet, apiPost, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { PaginatedResponse, TenantRef, UserKind } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

// `dashboard` is the friendly name for the admin-access bit; `curator` is
// deprecated and not exposed (such tokens report scope=null).
export type AccessScope = 'dashboard' | 'user' | 'booking' | 'console';

/** CPToken.AUTH_TYPES */
export const AUTH_TYPES: Record<number, string> = {
  0: 'Magic code',
  1: 'Short code',
  2: 'Password',
  3: 'Auth bypass',
  4: 'OAuth',
  5: 'Password + MFA',
  6: 'Sandbox sign-in from production',
};

export interface SessionRow {
  id: number;
  user: { id: number; email: string; kind: UserKind };
  tour_operator: TenantRef | null;
  access: { scope: AccessScope | null; flags: string[] };
  auth: number;
  auth_display: string;
  created: string;
  expires: string | null;
  is_expired: boolean;
  ttl_seconds: number | null;
  booking: { id: number; label: string } | null;
  token: { value: string; is_fingerprint: boolean };
}

export interface SessionListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  pageSize?: number;
  ordering?: string;
  access?: AccessScope[];
  auth?: number;
  user?: number;
  user_email?: string;
  booking?: number;
  /** One id or a comma list of ids (D1), or 'null' for system users (no tenant). */
  tour_operator?: TenantIdsParam;
  fingerprint?: string[];
  created_from?: string;
  created_to?: string;
  expires_from?: string;
  expires_to?: string;
  active?: boolean;
  /** Override the endpoint's own row-total mode. Omit to use the server
   *  default — that omission is what keeps a cold first load free. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
}

function toQuery(params: SessionListParams): URLSearchParams {
  const usp = new URLSearchParams();
  const pageSize = params.pageSize ?? 25;
  usp.set('limit', String(pageSize));
  if (params.page && params.page > 1) usp.set('offset', String((params.page - 1) * pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.timeout) usp.set('timeout', params.timeout);
  if (params.access?.length) usp.set('access', params.access.join(','));
  if (params.auth !== undefined) usp.set('auth', String(params.auth));
  if (params.user !== undefined) usp.set('user', String(params.user));
  if (params.user_email) usp.set('user_email', params.user_email);
  if (params.booking !== undefined) usp.set('booking', String(params.booking));
  if (params.tour_operator !== undefined) usp.set('tour_operator', String(params.tour_operator));
  if (params.fingerprint?.length) usp.set('fingerprint', params.fingerprint.join(','));
  for (const k of ['created_from', 'created_to', 'expires_from', 'expires_to'] as const) {
    const v = params[k];
    if (v) usp.set(k, v);
  }
  if (params.active !== undefined) usp.set('active', String(params.active));
  if (params.count_mode) usp.set('count_mode', params.count_mode);
  if (params.count_only) usp.set('count_only', '1');
  return usp;
}

export async function list(params: SessionListParams = {}): Promise<PaginatedResponse<SessionRow>> {
  return apiGet(`/sessions/?${toQuery(params).toString()}`, extendedListConfig(params.timeout));
}

/** Total only, for the grid's on-demand row count (computed in Celery). */
export async function count(params: SessionListParams = {}): Promise<CountOnlyResult> {
  const usp = toQuery({ ...params, count_only: true, count_mode: 'async' });
  return apiGet(`/sessions/?${usp.toString()}`);
}

export async function retrieve(id: number, reveal = false): Promise<SessionRow> {
  return apiGet(`/sessions/${id}/${reveal ? '?reveal=true' : ''}`);
}

export async function revoke(id: number): Promise<void> {
  await apiDelete<void>(`/sessions/${id}/`);
}

export async function extend(id: number, extendSeconds: number): Promise<SessionRow> {
  return apiPost(`/sessions/${id}/extend/`, { extend_seconds: extendSeconds });
}

export async function batch(
  action: 'revoke' | 'extend',
  ids: number[],
  extendSeconds?: number,
): Promise<{ affected: number; errors: { id: number; error: string }[] }> {
  return apiPost('/sessions/batch/', {
    action,
    ids,
    ...(extendSeconds !== undefined ? { extend_seconds: extendSeconds } : {}),
  });
}

export async function fingerprintSecret(): Promise<{ secret: string }> {
  return apiGet('/sessions/fingerprint_secret/');
}
